/**
 * Shared geometry for the overlaid native range sliders: RangeSlider.tsx
 * (v2 editors) and guide_meta.js + Guide/UI/templates/Guide/Intro/meta.phtml
 * (guide intro time estimate) draw the same thumb and fill.
 *
 * The Tailwind class list for the thumbs can't live here: tailwind-phtml.css only
 * scans .phtml files, so meta.phtml needs its literal copy, and the React
 * build needs the literal in RangeSlider.tsx. range_slider_style.test.ts
 * asserts the two copies stay in sync (modulo blue vs brand tokens).
 */

/**
 * Each thumb is a 24px hit target (WCAG 2.5.8) drawn as a 16px dot: a
 * transparent 4px border with the background clipped to the padding box.
 * Must match the size-6 thumb classes in RangeSlider.tsx and meta.phtml;
 * the fill math anchors to it.
 */
export const THUMB_PX = 24;

/**
 * Thumb centers travel within (100% - thumb width), so a fraction along the
 * track anchors to where the thumb's center actually lands rather than a raw
 * percentage, which drifts off the thumb near the ends. Shared by the fill
 * (fillStyle) and anything else that must line up with a thumb at rest, like
 * TimeSlider's tick labels.
 */
export function thumbCenterOffset(fraction: number): string {
   return `calc((100% - ${THUMB_PX}px) * ${fraction} + ${THUMB_PX / 2}px)`;
}

/**
 * Thumb centers travel within (100% - thumb width), so the fill anchors to
 * the centers rather than raw percentages, which drift off the thumbs near
 * the ends. A fill that starts at the track's left edge (single thumb)
 * instead runs edge-to-center.
 */
export function fillStyle(
   startFraction: number,
   endFraction: number,
   fillFromEdge: boolean
): { left: string; width: string } {
   const travel = `(100% - ${THUMB_PX}px)`;
   if (fillFromEdge) {
      return {
         left: '0',
         width: thumbCenterOffset(endFraction),
      };
   }
   return {
      left: thumbCenterOffset(startFraction),
      width: `calc(${travel} * ${endFraction - startFraction})`,
   };
}
